import { UnknownKeysParam, z, ZodEffects, ZodObject, ZodRawShape, ZodString, ZodTypeAny } from 'zod';
import { FormFieldConstraint, FormFieldType, FormFieldValue, isSelectField } from './FormValues';

const FORBIDDEN_REGEX_ERROR_MESSAGE = 'Forbidden input';

const validationRegex = {
    sqlInjectionPattern:
        /(\b(SELECT|UPDATE|DELETE|INSERT|DROP|ALTER|CREATE|TRUNCATE|EXEC|UNION|OR|AND)\b\s+\*|\b(SELECT|UPDATE|DELETE|INSERT|DROP|ALTER|CREATE|TRUNCATE|EXEC|UNION|OR|AND)\b\s+\w+\s*=\s*\w+|--|;|\/\*|\*\/)/gi,
    urlPattern: /(https?:\/\/|www\.)([^\s]+)/gi,
    htmlPattern: /<[^>]*>/gi,
};

const fieldSchemaGenerators: Record<
    FormFieldType,
    (field: FormFieldValue, constraints: Partial<FormFieldConstraint>) => ZodTypeAny
> = {
    [FormFieldType.TEXT]: (field, constraints) => {
        const minLength = constraints.minLength ?? field.minLength;
        const maxLength = constraints.maxLength ?? field.maxLength;
        const mandatoryRegex = constraints.regex?.mandatory;
        const forbiddenRegex = constraints.regex?.forbidden;

        let schema: ZodString | ZodEffects<ZodString> = z.string();

        if (minLength) schema = schema.min(minLength);
        if (maxLength) schema = schema.max(maxLength);
        if (mandatoryRegex) schema = schema.regex(mandatoryRegex);
        if (forbiddenRegex) {
            schema = schema.refine(
                (val) => {
                    // Manually reset the lastIndex of the regExp, to avoid false negatives
                    forbiddenRegex.lastIndex = 0;
                    return !forbiddenRegex.test(val);
                },
                {
                    message: FORBIDDEN_REGEX_ERROR_MESSAGE,
                },
            );
        }

        return schema;
    },
    [FormFieldType.NUMBER]: (_, constraints) => {
        let value = z.coerce.number();

        if (constraints.minValue !== undefined) {
            value = value.min(constraints.minValue);
        }

        if (constraints.maxValue !== undefined) {
            value = value.max(constraints.maxValue);
        }

        return value;
    },
    [FormFieldType.BOOLEAN]: () =>
        z.preprocess((val) => {
            if (typeof val === 'string') {
                return val.toLowerCase() === 'true';
            }
            return val;
        }, z.boolean()),
    [FormFieldType.SELECT]: (field, constraints) => {
        if (isSelectField(field) && field.options.length > 0 && constraints.isRequired) {
            const values = field.options.map((option) => option.value);
            return z.enum([...new Set(values)] as [string, ...string[]]);
        }

        return z.string();
    },
};

const generateFormSchema = <T extends Record<string, unknown>>(
    fields: FormFieldValue[],
    constraints?: Record<string, FormFieldConstraint>,
): ZodObject<ZodRawShape, UnknownKeysParam, ZodTypeAny, T> =>
    z.object(
        Object.fromEntries(
            fields.map((field) => {
                const fieldConstraints: Partial<FormFieldConstraint> = constraints?.[field.id] || {};
                const isRequired = fieldConstraints.isRequired ?? field.isRequired;

                const schemaGenerator = fieldSchemaGenerators[field.type];
                const schema = schemaGenerator ? schemaGenerator(field, fieldConstraints) : z.any();

                return [field.id, isRequired ? schema : schema.optional()];
            }),
        ) as ZodRawShape,
    ) as ZodObject<ZodRawShape, UnknownKeysParam, ZodTypeAny, T>;

export const FormUtils = {
    generateFormSchema,
    validationRegex,
};
